"use strict";

exports.__esModule = true;
exports.TIMER_FORMAT = exports.EXTENDED_FORMAT = void 0;
exports.formatDuration = formatDuration;
exports.formatDurationToParts = formatDurationToParts;
var _messages = _interopRequireDefault(require("./messages"));
var _formatMessageToParts = require("./formatMessageToParts");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { "default": e }; }
var EXTENDED_FORMAT = exports.EXTENDED_FORMAT = "EXTENDED_FORMAT";
var TIMER_FORMAT = exports.TIMER_FORMAT = "TIMER_FORMAT";

/**
 * Stringifies a duration in a localized format.
 *
 * @param intl       - React Intl intl instance
 * @param seconds    - Duration in seconds
 * @param format     - EXTENDED_FORMAT or TIMER_FORMAT
 * @returns {string} - The duration as a localized string
 */
function formatDuration(intl, seconds, _temp) {
  var _ref = _temp === void 0 ? {} : _temp,
    format = _ref.format;
  return formatDurationToParts(intl, seconds, {
    format: format
  }).map(function (token) {
    return token.value;
  }).join("").trim();
}

/**
 * Generate a duration as an array of tokens. Similar to {@link Intl.DateTimeFormat#formatToParts}
 *
 * @example formatDurationToParts(intl, 150, { format: 'EXTENDED_FORMAT' })
 * [ { type: 'minutes', value: '2' },
 *   { type: 'literal', value: ' ' },
 *   { type: 'unit', value: 'minutes' },
 *   { type: 'literal', value: ' ' },
 *   { type: 'seconds', value: '30' },
 *   { type: 'literal', value: ' ' },
 *   { type: 'unit', value: 'seconds' } ]
 *
 * @example formatDurationToParts(intl, 62, { format: 'TIMER_FORMAT' })
 * [ { type: 'minutes', value: '1' },
 *   { type: 'literal', value: ':' },
 *   { type: 'seconds', value: '02' } ]
 *
 * @param intl      - React Intl intl instance
 * @param seconds   - Duration in seconds
 * @param format    - EXTENDED_FORMAT or TIMER_FORMAT
 * @returns {Array} - The duration string as an array of { type, value } tokens.
 */
function formatDurationToParts(intl, seconds, _temp2) {
  var _ref2 = _temp2 === void 0 ? {} : _temp2,
    format = _ref2.format;
  var fullDays = Math.floor(seconds / 86400);
  var fullHours = Math.floor(seconds / 3600);
  var hoursLeft = Math.floor(fullHours % 24);
  var fullMinutes = Math.floor(seconds / 60);
  var minutesLeft = Math.floor(fullMinutes % 60);
  var secondsLeft = seconds % 60;
  var valueFormatter;
  var formattingMessage;
  switch (format) {
    case TIMER_FORMAT:
      valueFormatter = padValue;
      formattingMessage = _messages["default"].timerFormatting;
      break;
    case EXTENDED_FORMAT:
      valueFormatter = formatUnit;
      formattingMessage = _messages["default"].longFormatting;
      break;
    default:
      valueFormatter = formatUnit;
      formattingMessage = format ? {
        id: "react-intl-formatted-duration/custom-format/" + format,
        defaultMessage: format
      } : _messages["default"].longFormatting;
      break;
  }
  var message = intl.messages[formattingMessage.id] || formattingMessage.defaultMessage || "";
  var hasSeconds = message.indexOf("{seconds}") !== -1;
  var hasHours = message.indexOf("{hours}") !== -1;
  var hasDays = message.indexOf("{days}") !== -1;
  var showFullMinutes = !hasHours && !hasDays;
  var showFullHours = !hasDays;
  var tokenizedMessage = (0, _formatMessageToParts.formatMessageToParts)(intl, formattingMessage, {
    days: hasDays ? valueFormatter({
      intl: intl,
      value: fullDays,
      key: "days",
      maxLengthIfPadded: 1,
      showIfZero: false,
      message: _messages["default"].daysUnit
    }) : undefined,
    hours: hasHours ? valueFormatter({
      intl: intl,
      value: showFullHours ? fullHours : hoursLeft,
      key: "hours",
      maxLengthIfPadded: 1,
      showIfZero: false,
      message: _messages["default"].hoursUnit
    }) : undefined,
    minutes: valueFormatter({
      intl: intl,
      value: (showFullMinutes ? fullMinutes : minutesLeft) + (!hasSeconds && secondsLeft >= 30 ? 1 : 0),
      key: "minutes",
      maxLengthIfPadded: 1,
      showIfZero: !hasSeconds && fullHours === 0,
      message: _messages["default"].minutesUnit
    }),
    seconds: hasSeconds ? valueFormatter({
      intl: intl,
      value: secondsLeft,
      key: "seconds",
      maxLengthIfPadded: 2,
      showIfZero: false,
      message: _messages["default"].secondsUnit
    }) : undefined
  });

  // formatUnit generates more tokenized messages, we need to flatten them
  if (valueFormatter === formatUnit) {
    var flatTokenizedMessage = [];
    for (var i = 0; i < tokenizedMessage.length; i++) {
      var token = tokenizedMessage[i];
      var subTokens = token.value;
      if (!Array.isArray(subTokens)) {
        flatTokenizedMessage.push(token);
        continue;
      }
      for (var j = 0; j < subTokens.length; j++) {
        var subToken = subTokens[j];
        if (subToken.type === "value") {
          subToken.type = token.type;
        }
        flatTokenizedMessage.push(subToken);
      }
    }
    tokenizedMessage = flatTokenizedMessage;
  }

  // Remove trailing spaces (start)
  while (tokenizedMessage.length > 0 && tokenizedMessage[0].value.trim() === "") {
    tokenizedMessage.shift();
  }

  // Remove trailing spaces (end)
  while (tokenizedMessage.length > 0 && tokenizedMessage[tokenizedMessage.length - 1].value.trim() === "") {
    tokenizedMessage.pop();
  }

  // Concat consecutive literals & reduce spaces to 1
  for (var _i = 0; _i < tokenizedMessage.length - 1; _i++) {
    var _token = tokenizedMessage[_i];
    if (_token.type !== _formatMessageToParts.KEY_LITERAL) {
      continue;
    }
    var nextToken = tokenizedMessage[_i + 1];
    if (nextToken.type !== _formatMessageToParts.KEY_LITERAL) {
      _token.type = "literal";
      _i++; // skip next token too
      continue;
    }
    _token.value = (_token.value + nextToken.value).replace(/\s\s+/g, " ");
    tokenizedMessage.splice(_i + 1, 1);
    _i--; // stay on current token.
  }
  return tokenizedMessage;
}
function formatUnit(_ref3) {
  var intl = _ref3.intl,
    value = _ref3.value,
    showIfZero = _ref3.showIfZero,
    message = _ref3.message;
  if (!value && !showIfZero) {
    return [];
  }
  value = String(value);
  var unit = intl.formatMessage(message, {
    value: value
  });
  return (0, _formatMessageToParts.formatMessageToParts)(intl, _messages["default"].duration, {
    value: value,
    unit: unit
  });
}
function padValue(_ref4) {
  var value = _ref4.value,
    maxLengthIfPadded = _ref4.maxLengthIfPadded;
  return ("0" + (value || "0")).substr(-maxLengthIfPadded);
}